/**
 * useSignedContracts
 * Lista los contratos firmados del usuario (todos los créditos) y maneja
 * el state de un visor inline (modal con iframe).
 *
 * Endpoint: GET /credit/backoffice/directories/{directoryId}/signed-contracts
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getSignedContracts,
  getSignedContractPdf,
} from '@/services/credit-engine.service';
import { PARTNER_ID } from '@/constants';
import type { SignedContractListItem } from '@/types/credit.types';

export function useSignedContracts(directoryId: string) {
  const [viewer, setViewer] = useState<{
    isOpen: boolean;
    title: string;
    url: string | null;
    isLoading: boolean;
    contractId: string | null;
  }>({ isOpen: false, title: '', url: null, isLoading: false, contractId: null });

  const query = useQuery({
    queryKey: ['credit', 'signed-contracts', directoryId],
    queryFn: () => getSignedContracts(directoryId),
    enabled: Boolean(directoryId),
  });

  const handleView = async (item: SignedContractListItem) => {
    setViewer({
      isOpen: true,
      title: humanizeContractType(item.contract_type),
      url: null,
      isLoading: true,
      contractId: item.contract_id,
    });
    try {
      const result = await getSignedContractPdf(item.contract_id, PARTNER_ID);
      setViewer((prev) => ({ ...prev, url: result.data?.url ?? null, isLoading: false }));
    } catch (error) {
      console.error('Error fetching signed contract PDF', error);
      setViewer((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const closeViewer = () => {
    setViewer({ isOpen: false, title: '', url: null, isLoading: false, contractId: null });
  };

  return {
    contracts: query.data?.data?.contracts ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    viewer,
    handleView,
    closeViewer,
  };
}

const HUMAN_LABELS: Record<string, string> = {
  CREDIT_CONTRATO_SIMPLE: 'Contrato de Crédito Simple',
  CREDIT_CONTRACT_FULL: 'Contrato de Crédito',
  CREDIT_CARTA_CESION: 'Carta de Cesión',
  CREDIT_CONSTANCIA_CESION_PARCIAL: 'Constancia de Cesión Parcial',
  CREDIT_TABLA_AMORTIZACION: 'Tabla de Amortización',
  CREDIT_ANEXO_LEGAL: 'Anexo Legal',
  CREDIT_CARATULA: 'Carátula',
  CREDIT_MONTHLY_STATEMENT: 'Estado de Cuenta',
  TERMS_WALLET_PAGO: 'Términos Wallet Pago',
  TERMS_WALLET_PAGO_DOMAUT: 'Términos Wallet Pago (Domiciliación)',
  TERMS_WALLET_PAGO_BENEF: 'Términos Wallet Pago (Beneficiario)',
};

export function humanizeContractType(type: string): string {
  return HUMAN_LABELS[type] || type;
}
